import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Video, Mic, Square, Upload, Camera, Ruler, Loader2,
  ClipboardList, Wrench, FileText, AlertTriangle, Maximize2, X
} from "lucide-react";

export function WalkthroughButton({ entityType, entityId }) {
  const location = useLocation();
  const hideOnMobile = location.pathname.endsWith("/design-estimate") || location.pathname === "/quick-quote";
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("record");

  // Recording state
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  // Transcription state
  const [transcribing, setTranscribing] = useState(false);
  const [result, setResult] = useState(null);

  // Photo measurement state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [measuring, setMeasuring] = useState(false);
  const [measurements, setMeasurements] = useState(null);
  const fileInputRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };

      recorder.start(1000);
      setMediaRecorder(recorder);
      setRecording(true);
      setRecordingTime(0);
      setResult(null);

      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch (err) {
      toast.error("Microphone access denied. Please allow microphone access.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const handleTranscribe = async () => {
    if (!audioBlob) return;
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "walkthrough.webm");
      if (entityType) formData.append("entity_type", entityType);
      if (entityId) formData.append("entity_id", entityId);
      formData.append("auto_categorize", "true");

      const res = await axios.post(`${API}/walkthrough/transcribe`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(res.data);
      toast.success("Walkthrough transcribed and categorized!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setMeasurements(null);
  };

  const handleMeasurePhoto = async () => {
    if (!photoFile) return;
    setMeasuring(true);
    try {
      const formData = new FormData();
      formData.append("photo", photoFile);
      if (entityType) formData.append("entity_type", entityType);
      if (entityId) formData.append("entity_id", entityId);

      const res = await axios.post(`${API}/walkthrough/measure-photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setMeasurements(res.data);
      toast.success("Photo analyzed for measurements!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Measurement failed");
    } finally {
      setMeasuring(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cat = result?.categories;

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-20 right-6 z-40 w-14 h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg items-center justify-center transition-transform hover:scale-110 ${hideOnMobile ? "hidden sm:flex" : "flex"}`}
        data-testid="walkthrough-fab"
        title="Site Walkthrough"
      >
        <Video className="w-6 h-6" />
      </button>

      {/* Main Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-indigo-600" />
              Site Walkthrough Tools
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="record" className="flex-1 gap-1"><Mic className="w-3 h-3" /> Record & Transcribe</TabsTrigger>
              <TabsTrigger value="measure" className="flex-1 gap-1"><Ruler className="w-3 h-3" /> Photo Measure</TabsTrigger>
            </TabsList>

            {/* Record & Transcribe Tab */}
            <TabsContent value="record" className="space-y-4 mt-4">
              {/* Recorder */}
              <div className="flex flex-col items-center gap-4 p-6 bg-slate-50 rounded-lg">
                {!recording && !audioBlob && (
                  <>
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                      <Mic className="w-10 h-10 text-red-500" />
                    </div>
                    <p className="text-sm text-slate-600 text-center">Tap to start recording your walkthrough. Speak naturally about what you see — notes, measurements, tasks, materials needed.</p>
                    <Button onClick={startRecording} className="bg-red-500 hover:bg-red-600" data-testid="start-recording">
                      <Mic className="w-4 h-4 mr-2" /> Start Recording
                    </Button>
                  </>
                )}

                {recording && (
                  <>
                    <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                      <Mic className="w-10 h-10 text-white" />
                    </div>
                    <p className="text-2xl font-mono font-bold text-red-600">{formatTime(recordingTime)}</p>
                    <p className="text-sm text-slate-500">Recording... speak about what you see</p>
                    <Button onClick={stopRecording} variant="outline" className="border-red-300 text-red-600" data-testid="stop-recording">
                      <Square className="w-4 h-4 mr-2" /> Stop Recording
                    </Button>
                  </>
                )}

                {audioBlob && !recording && (
                  <>
                    <p className="text-sm text-green-600 font-medium">Recording complete ({formatTime(recordingTime)})</p>
                    <div className="flex gap-2">
                      <Button onClick={() => { setAudioBlob(null); setResult(null); setRecordingTime(0); }} variant="outline" size="sm">
                        Re-record
                      </Button>
                      <Button onClick={handleTranscribe} disabled={transcribing} data-testid="transcribe-btn">
                        {transcribing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcribing...</> : <><Upload className="w-4 h-4 mr-2" /> Transcribe & Categorize</>}
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Results */}
              {result && (
                <div className="space-y-4">
                  {/* Transcript */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Full Transcript</p>
                    <div className="bg-white border rounded-lg p-3 text-sm text-slate-700 max-h-32 overflow-y-auto">
                      {result.transcript}
                    </div>
                  </div>

                  {/* Categories */}
                  {cat && (
                    <>
                      {cat.summary && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                          <p className="text-sm font-medium text-indigo-700">{cat.summary}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CategoryCard icon={FileText} title="Notes" items={cat.notes} color="blue" />
                        <CategoryCard icon={ClipboardList} title="Tasks" items={cat.tasks} color="green" />
                        <CategoryCard icon={Ruler} title="Measurements" items={cat.measurements} color="purple" />
                        <CategoryCard icon={Wrench} title="Materials" items={cat.materials} color="orange" />
                        <CategoryCard icon={AlertTriangle} title="Issues" items={cat.issues} color="red" />
                      </div>
                    </>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Photo Measure Tab */}
            <TabsContent value="measure" className="space-y-4 mt-4">
              <div className="flex flex-col items-center gap-4 p-6 bg-slate-50 rounded-lg">
                {!photoPreview ? (
                  <>
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                      <Camera className="w-10 h-10 text-blue-500" />
                    </div>
                    <p className="text-sm text-slate-600 text-center">Take a photo or upload one. AI will estimate room and object dimensions. For better accuracy, include a tape measure or ruler in the photo.</p>
                    <div className="flex gap-2">
                      <Button onClick={() => fileInputRef.current?.click()} data-testid="upload-photo-btn">
                        <Camera className="w-4 h-4 mr-2" /> Select Photo
                      </Button>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoSelect} />
                  </>
                ) : (
                  <>
                    <img src={photoPreview} alt="Preview" className="max-h-48 rounded-lg border" />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setPhotoFile(null); setPhotoPreview(null); setMeasurements(null); }}>
                        Change Photo
                      </Button>
                      <Button onClick={handleMeasurePhoto} disabled={measuring} data-testid="measure-btn">
                        {measuring ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <><Ruler className="w-4 h-4 mr-2" /> Estimate Measurements</>}
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Measurement Results */}
              {measurements && (
                <div className="space-y-3">
                  {measurements.room_estimate && Object.keys(measurements.room_estimate).length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-blue-600 uppercase mb-2">Room Estimate</p>
                      <div className="flex gap-4 text-sm">
                        {Object.entries(measurements.room_estimate).map(([k, v]) => (
                          <div key={k}><span className="text-slate-500 capitalize">{k}:</span> <span className="font-medium">{v}</span></div>
                        ))}
                      </div>
                    </div>
                  )}

                  {measurements.objects && measurements.objects.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Detected Objects</p>
                      <div className="space-y-2">
                        {measurements.objects.map((obj, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white border rounded text-sm">
                            <span className="font-medium">{obj.name}</span>
                            <div className="flex items-center gap-3">
                              {obj.estimated_width && <span className="text-slate-600">W: {obj.estimated_width}</span>}
                              {obj.estimated_height && <span className="text-slate-600">H: {obj.estimated_height}</span>}
                              <Badge variant="outline" className={`text-xs ${obj.confidence === "high" ? "text-green-600" : obj.confidence === "medium" ? "text-yellow-600" : "text-red-600"}`}>
                                {obj.confidence}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {measurements.reference_object_found === false && (
                    <p className="text-xs text-amber-600 bg-amber-50 rounded p-2">
                      No reference object detected. Estimates are based on standard construction dimensions. For better accuracy, include a tape measure or ruler in the photo.
                    </p>
                  )}

                  {measurements.notes && (
                    <div className="text-sm text-slate-600 bg-slate-50 rounded p-3">{measurements.notes}</div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CategoryCard({ icon: Icon, title, items, color }) {
  if (!items || items.length === 0) return null;
  const colors = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-green-50 border-green-200 text-green-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    red: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div className={`border rounded-lg p-3 ${colors[color] || ""}`}>
      <p className="text-xs font-semibold uppercase flex items-center gap-1 mb-2">
        <Icon className="w-3 h-3" /> {title} ({items.length})
      </p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-xs">{item}</li>
        ))}
      </ul>
    </div>
  );
}
